import type { ModelRole } from "../models.ts";
import { CANONICAL_MODELS } from "./canonical.generated.ts";
import { BEDROCK_OFFERINGS } from "./offerings.ts";

/**
 * The capability gate (openspec change `model-picking`, D4 amended by Q18b): what each role
 * needs from a model, checked in `deriveCandidates` before health, so no effort map, pin or
 * allowed-models entry puts a model in a turn it cannot serve.
 */

/**
 * The smallest context window an agent role accepts. Code has no single turn budget to derive
 * it from (a turn holds the history, up to 32 000 inline attachment tokens and up to 20 steps of
 * tool results), so it is set at the smallest window of the pinned snapshot's served models
 * that ESI considers usable for a turn (128 000: Nova Micro, Pixtral Large); a smaller one would
 * fail long turns.
 */
export const MIN_AGENT_CONTEXT = 128_000;

export interface RoleNeeds {
  /** The model must declare tool use (canonical `toolCall`). */
  readonly toolUse: boolean;
  /** Its context window (canonical `limit.context`) must be at least this. */
  readonly minContext: number;
  /** The API streams these turns: a route Bedrock lists without response streaming is out. */
  readonly streaming: boolean;
  /** It must accept image input (canonical `modalities.input`). */
  readonly imageInput: boolean;
}

const AGENT: RoleNeeds = {
  toolUse: true,
  minContext: MIN_AGENT_CONTEXT,
  streaming: true,
  imageInput: false,
};

/**
 * What each role needs (data). The agent roles (`chat`, `reasoning`, `fast`) serve turns and
 * the tool-using utilities; utility tasks without tools call the same roles, so they get the
 * agent's needs too (the strictest, never looser). `vision` describes images: image input only.
 */
export const ROLE_NEEDS: Readonly<Record<ModelRole, RoleNeeds>> = {
  chat: AGENT,
  reasoning: AGENT,
  fast: AGENT,
  vision: { toolUse: false, minContext: 0, streaming: false, imageInput: true },
};

/** What discovery and the probe observed of one route (D6): unknown fields are not lacking. */
export interface ObservedCapabilities {
  /** `ListFoundationModels`' `responseStreamingSupported`; null or absent: unknown. */
  readonly streaming?: boolean | null;
  /** The probe found the route refuses a tool configuration. */
  readonly unsupportedTools?: boolean;
  /** The probe found the route refuses a streamed call. */
  readonly unsupportedStreaming?: boolean;
}

/** Whether the catalogue says a model uses tools (canonical `toolCall`; its probe checks it). */
export function declaresToolUse(key: string): boolean {
  const offering = (BEDROCK_OFFERINGS as Readonly<Record<string, { canonical: string }>>)[key];
  return offering ? CANONICAL_MODELS[offering.canonical]?.toolCall === true : false;
}

/** Why a catalogued model cannot serve `role`, or undefined when it can. */
export function capabilityGap(
  key: string,
  role: ModelRole,
  observed: ObservedCapabilities = {},
): "no_tool_use" | "context_too_small" | "no_streaming" | "no_image_input" | undefined {
  const offering = (BEDROCK_OFFERINGS as Readonly<Record<string, { canonical: string }>>)[key];
  const canonical = offering ? CANONICAL_MODELS[offering.canonical] : undefined;
  if (!canonical) {
    return "no_tool_use";
  }
  const needs = ROLE_NEEDS[role];
  if (needs.toolUse && (canonical.toolCall !== true || observed.unsupportedTools === true)) {
    return "no_tool_use";
  }
  if ((canonical.limit?.context ?? 0) < needs.minContext) {
    return "context_too_small";
  }
  if (needs.streaming && (observed.streaming === false || observed.unsupportedStreaming === true)) {
    return "no_streaming";
  }
  if (needs.imageInput && !canonical.modalities.input.includes("image")) {
    return "no_image_input";
  }
  return undefined;
}

/** Whether a catalogued model can serve `role` (the gate, D4). */
export function meetsRoleNeeds(
  key: string,
  role: ModelRole,
  observed?: ObservedCapabilities,
): boolean {
  return capabilityGap(key, role, observed) === undefined;
}
